import type { NavItems } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import { useMemo } from 'react';
import type { Profession } from '@common/enums';
import { EpisodeCategory, ManagerAuthorization, ServiceCode } from '@common/enums';
import { hasBrightPlanAccess } from '@frontend/services/authorizationService';
import { getJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization, useAbortableFetch } from '@frontend/utils/';
import { handleBrightPlanGetSsoUrl } from '@frontend/utils/handleBrightPlanGetSsoUrl';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import { submitSamlForm } from '@frontend/utils/submitSamlForm';
import type { IOrganization, IServiceConsumer, IServiceEmployment } from '../../../interfaces/';
import { groupNavigationItems } from '../utils/groupNavigationItems';

export type UseNavigationItemsProps = {
    bariPostSettings?: IServiceEmployment['settings'];
    bariPreSettings?: IServiceEmployment['settings'];
    currentOrganization: Pick<IOrganization, 'id' | 'name' | 'suppressEmployeePayoutOverview'>;
    currentProfession: Profession;
    hasAccessToFundusNoShows: boolean;
    hasAccessToHolterDashboard: boolean;
    hasAccessToHomepage: boolean;
    hasAccessToOutpatientClinicTaskList: boolean;
    isKsyos: boolean;
    isManager: boolean;
    isOnlyOrganizationPayoutManager: boolean;
    osasSettings?: IServiceEmployment['settings'];
    serviceConsumers: IServiceConsumer[];
};

export function useNavigationItems({
    hasAccessToHomepage,
    hasAccessToHolterDashboard,
    hasAccessToFundusNoShows,
    hasAccessToOutpatientClinicTaskList,
    currentOrganization,
    currentProfession,
    bariPreSettings,
    bariPostSettings,
    osasSettings,
    isOnlyOrganizationPayoutManager,
    isManager,
    isKsyos,
    serviceConsumers,
}: UseNavigationItemsProps) {
    const { frontendSettings } = useFrontendSettings();
    const language = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    async function getChatbotSsoUrl() {
        const { json: result } = await getJson('/api/chatbot/getSsoUrl');

        if (result.ssoUrl) {
            window.open(result.ssoUrl);
        }
    }

    async function deletePractitionerFirstUseTourSteps() {
        await abortableFetch.fetchJson('/api/practitionerFirstUseTourStep', 'DELETE');
    }

    async function handleTherapielandSsoLinkClick() {
        const { json: result } = await abortableFetch.fetchJson('/api/therapieland/getSso', 'GET');
        submitSamlForm({
            context: result.context,
            endpoint: result.endpoint,
        });
    }

    return useMemo(() => {
        const items: NavItems[] = [];

        if (hasAccessToHomepage) {
            items.push({
                icon: 'home',
                key: 'home',
                label: language.common.home,
                to: '/home',
                variant: 'link',
            });
        }

        const dashboardItems: NavItems[] = [];
        const showBari = bariPreSettings?.canReadBariPreDashboard || bariPostSettings?.canReadDashboard;
        const showOsas = osasSettings?.canReadOsasDashboard;

        if (showOsas) {
            dashboardItems.push({
                key: `dashboard-${EpisodeCategory.somnology}`,
                label: language.episodeCategoryEnum.somnology,
                to: `/dashboard/${EpisodeCategory.somnology}`,
                variant: 'link',
            });
        }

        if (showBari) {
            dashboardItems.push({
                key: `dashboard-${EpisodeCategory.bariatrics}`,
                label: language.episodeCategoryEnum.bariatrics,
                to: `/dashboard/${EpisodeCategory.bariatrics}`,
                variant: 'link',
            });
        }

        const groupedDashboardItems = groupNavigationItems({
            icon: 'dashboard',
            items: dashboardItems,
            label: language.common.dashboard,
        });

        if (groupedDashboardItems) {
            items.push(groupedDashboardItems);
        }

        const taskItems: NavItems[] = [];

        if (!isManager && !isOnlyOrganizationPayoutManager) {
            taskItems.push({
                key: 'requests',
                label: language.outpatientClinic.tasks.allTasks,
                to: '/requests',
                variant: 'link',
            });
        }

        if (hasAccessToOutpatientClinicTaskList) {
            taskItems.push({
                key: 'outpatientClinic',
                label: ` ${language.request.patientTasks} beta`,
                to: '/outpatientClinic',
                variant: 'link',
            });
        }

        const groupedTaskItems = groupNavigationItems({
            icon: 'todo',
            items: taskItems,
            label: language.common.tasks,
        });

        if (groupedTaskItems) {
            items.push(groupedTaskItems);
        }

        if (!isManager && !isOnlyOrganizationPayoutManager) {
            items.push({
                icon: 'patients',
                key: 'patients',
                label: language.patient.patients,
                to: '/patients',
                variant: 'link',
            });
        }

        if (bariPreSettings?.canReadMdo) {
            items.push({
                icon: 'patients',
                key: 'mdo',
                label: 'MDO',
                to: '/mdoManagement',
                variant: 'link',
            });
        }

        if (bariPreSettings?.canReadGroupSession) {
            items.push({
                icon: 'calender',
                key: 'groupSessions',
                label: 'Groepsvoorlichting',
                to: '/groupSessionManagement',
                variant: 'link',
            });
        }

        if (bariPostSettings?.canExportForDato) {
            items.push({
                icon: 'download',
                key: 'export',
                label: 'Exporteren',
                to: '/exports/dato',
                variant: 'link',
            });
        }

        if (isOnlyOrganizationPayoutManager) {
            items.push({
                icon: 'healthcare-institution',
                key: 'organization',
                label: language.organization.organization,
                to: `/organizations/${currentOrganization.id}/view`,
                variant: 'link',
            });
        }

        if (!isOnlyOrganizationPayoutManager && hasAccessToHolterDashboard) {
            items.push({
                icon: 'recorder',
                key: 'holterDashboard',
                label: language.holter.recorders,
                to: '/holter/dashboard',
                variant: 'link',
            });
        }

        if (!isOnlyOrganizationPayoutManager && hasAccessToFundusNoShows && !isKsyos) {
            items.push({
                icon: 'information',
                key: 'fundusNoShows',
                label: language.common.noShows,
                to: '/fundusNoShows',
                variant: 'link',
            });
        }

        const serviceConsumerCounseling = serviceConsumers.find(
            (serviceConsumer) =>
                serviceConsumer.service.code === ServiceCode.GGZBther &&
                (!serviceConsumer.endDate || DateTime.now().isSameOrBefore(serviceConsumer.endDate))
        );

        if (serviceConsumerCounseling) {
            const serviceCode = serviceConsumerCounseling.service.code;

            if (serviceCode === ServiceCode.GGZBther) {
                items.push({
                    icon: 'external',
                    key: 'therapieland',
                    label: language.common.goTo.replace('{0}', 'Therapieland'),
                    onClick: async () => await handleTherapielandSsoLinkClick(),
                    variant: 'button',
                });
            }
        }

        if (hasOneAuthorization(ManagerAuthorization.organizationRead)) {
            items.push({
                icon: 'organization',
                key: 'organizations',
                label: language.organization.organizations,
                to: '/organizations',
                variant: 'link',
            });
        }

        if (hasOneAuthorization(ManagerAuthorization.practitionerRead)) {
            items.push({
                icon: 'practitioner',
                key: 'practitioners',
                label: language.practitioner.practitioners,
                to: '/practitioners',
                variant: 'link',
            });
        }

        if (hasOneAuthorization(ManagerAuthorization.adminProcessRead)) {
            items.push({
                icon: 'jobs',
                key: 'processes',
                label: language.request.processes,
                to: '/processes',
                variant: 'link',
            });
        }

        if (hasOneAuthorization(ManagerAuthorization.devicesRead)) {
            items.push({
                icon: 'devices',
                key: 'devices',
                label: 'Devices',
                to: '/devices',
                variant: 'link',
            });
        }

        if (
            hasOneAuthorization([
                ManagerAuthorization.exportInvoiceRecords,
                ManagerAuthorization.financeFunding,
                ManagerAuthorization.financePayoutsRead,
                ManagerAuthorization.reportFinancialComplete,
                ManagerAuthorization.reportFunding,
                ManagerAuthorization.reportPayouts,
                ManagerAuthorization.exportPayoutRecords,
                ManagerAuthorization.exportDeclarationRecords,
            ])
        ) {
            items.push({
                icon: 'finance',
                items: [
                    /**
                     * It is assumed that the following commented-out reports are not needed anymore.
                     * See https://ksyos-it.atlassian.net/browse/CS-26456
                     */
                    // ...(hasOneAuthorization([
                    //     ManagerAuthorization.reportFinancialComplete,
                    //     ManagerAuthorization.reportFunding,
                    //     ManagerAuthorization.reportPayouts,
                    // ])
                    //     ? [
                    //           {
                    //               items: [
                    //                   ...(hasOneAuthorization(ManagerAuthorization.reportFinancialComplete)
                    //                       ? [
                    //                             {
                    //                                 key: 'finance-reports-complete',
                    //                                 label: language.finance.declarationInvoices,
                    //                                 to: '/finance/reports/complete',
                    //                                 variant: 'link' as const,
                    //                             },
                    //                         ]
                    //                       : []),
                    //                   ...(hasOneAuthorization(ManagerAuthorization.reportPayouts)
                    //                       ? [
                    //                             {
                    //                                 key: 'finance-reports-payouts',
                    //                                 label: language.finance.payout,
                    //                                 to: '/finance/reports/payouts',
                    //                                 variant: 'link' as const,
                    //                             },
                    //                         ]
                    //                       : []),
                    //                   ...(hasOneAuthorization(ManagerAuthorization.reportFunding)
                    //                       ? [
                    //                             {
                    //                                 key: 'finance-reports-funding',
                    //                                 label: language.finance.financing,
                    //                                 to: '/finance/reports/funding',
                    //                                 variant: 'link' as const,
                    //                             },
                    //                         ]
                    //                       : []),
                    //               ],
                    //               key: 'finance-reports',
                    //               label: language.finance.reports,
                    //               variant: 'button' as const,
                    //           },
                    //       ]
                    //     : []),

                    ...(hasOneAuthorization(ManagerAuthorization.financePayoutsRead)
                        ? [
                              {
                                  key: 'finance-payments',
                                  label: language.finance.payments,
                                  to: '/finance/payments',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),

                    ...(hasOneAuthorization([
                        ManagerAuthorization.exportPayoutRecords,
                        ManagerAuthorization.exportDeclarationRecords,
                        ManagerAuthorization.exportInvoiceRecords,
                        ManagerAuthorization.exportUbd,
                    ])
                        ? [
                              {
                                  items: [
                                      ...(hasOneAuthorization(ManagerAuthorization.exportPayoutRecords)
                                          ? [
                                                {
                                                    key: 'finance-exports-payments',
                                                    label: language.finance.payments,
                                                    to: '/finance/export/payments',
                                                    variant: 'link' as const,
                                                },
                                            ]
                                          : []),
                                      ...(hasOneAuthorization(ManagerAuthorization.exportDeclarationRecords)
                                          ? [
                                                {
                                                    key: 'finance-exports-declarations',
                                                    label: language.finance.declarations,
                                                    to: '/finance/export/declarations',
                                                    variant: 'link' as const,
                                                },
                                            ]
                                          : []),
                                      ...(hasOneAuthorization(ManagerAuthorization.exportInvoiceRecords)
                                          ? [
                                                {
                                                    key: 'finance-exports-invoiceRecords',
                                                    label: language.finance.invoices,
                                                    to: '/finance/export/invoiceRecords',
                                                    variant: 'link' as const,
                                                },
                                            ]
                                          : []),
                                      ...(hasOneAuthorization(ManagerAuthorization.exportUbd)
                                          ? [
                                                {
                                                    key: 'finance-exports-ubd',
                                                    label: language.finance.employmentTaxesDetailsExport.ubd,
                                                    to: '/finance/export/ubd',
                                                    variant: 'link' as const,
                                                },
                                            ]
                                          : []),
                                  ],
                                  key: 'finance-exports',
                                  label: language.finance.exports,
                                  variant: 'button' as const,
                              },
                          ]
                        : []),

                    ...(hasOneAuthorization(ManagerAuthorization.financeFunding)
                        ? [
                              {
                                  key: 'finance-financing',
                                  label: language.finance.financing,
                                  to: '/finance/financing',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                ],
                key: 'finance',
                label: language.finance.finance,
                variant: 'button',
            });
        }

        if (hasOneAuthorization(ManagerAuthorization.jobs)) {
            items.push({
                icon: 'jobs',
                key: 'jobs',
                label: language.jobs.jobs,
                to: '/jobs',
                variant: 'link',
            });
        }

        if (
            hasOneAuthorization([
                ManagerAuthorization.patientAccessLog,
                ManagerAuthorization.dermaAppRegistrationLogs,
                ManagerAuthorization.desktopProcessesLog,
                ManagerAuthorization.thirdPartyIncomingMessages,
                ManagerAuthorization.thirdPartyMessages,
                ManagerAuthorization.fundingLogRead,
            ])
        ) {
            items.push({
                icon: 'records',
                items: [
                    ...(hasOneAuthorization(ManagerAuthorization.thirdPartyMessages)
                        ? [
                              {
                                  key: 'thirdPartyMessages',
                                  label: language.logs.thirdPartyMessages,
                                  to: '/thirdPartyMessages',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.patientAccessLog)
                        ? [
                              {
                                  key: 'patientAccess',
                                  label: language.patientAccess.patientAccessLog,
                                  to: '/patientAccess',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.desktopProcessesLog)
                        ? [
                              {
                                  key: 'desktopProcesses',
                                  label: language.desktopProcess.desktopProcesses,
                                  to: '/desktopProcesses',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.thirdPartyIncomingMessages)
                        ? [
                              {
                                  key: 'incomingMessages',
                                  label: language.incomingMessages.incomingMessages,
                                  to: '/incomingMessages',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.dermaAppRegistrationLogs)
                        ? [
                              {
                                  key: 'dermaAppRegistrations',
                                  label: language.dermaApp.registrations,
                                  to: '/dermaAppRegistrations',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.fundingLogRead)
                        ? [
                              {
                                  key: 'fundingLogs',
                                  label: language.fundingLog.fundingLog,
                                  to: '/fundingLogs',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                ],
                key: 'logging',
                label: language.common.logging,
                variant: 'button',
            });
        }

        if (!isOnlyOrganizationPayoutManager && hasAccessToFundusNoShows && isKsyos) {
            items.push({
                icon: 'information',
                key: 'fundusNoShows',
                label: language.common.noShows,
                to: '/fundusNoShows',
                variant: 'link',
            });
        }

        if (
            hasOneAuthorization([
                ManagerAuthorization.importBankAccounts,
                ManagerAuthorization.importDhd,
                ManagerAuthorization.importExactRelationData,
                ManagerAuthorization.importNoShows,
                ManagerAuthorization.importHardwareTokens,
                ManagerAuthorization.fundusScreeningManualImport,
            ])
        ) {
            items.push({
                icon: 'import',
                items: [
                    ...(hasOneAuthorization(ManagerAuthorization.importDhd)
                        ? [
                              {
                                  key: 'import-dhd',
                                  label: language.imports.dhd,
                                  to: '/import/importDhd',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.importNoShows)
                        ? [
                              {
                                  key: 'import-noShows',
                                  label: language.imports.noShows,
                                  to: '/import/importNoShows',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.importExactRelationData)
                        ? [
                              {
                                  key: 'import-relationNumbers',
                                  label: language.imports.relationNumbers,
                                  to: '/import/importRelationNumbers',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.fundusScreeningManualImport)
                        ? [
                              {
                                  key: 'import-fundusScreening',
                                  label: language.imports.fundusScreening,
                                  to: '/import/fundusScreening',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.importBankAccounts)
                        ? [
                              {
                                  key: 'import-bankAccountDetails',
                                  label: language.imports.bankAccountDetails,
                                  to: '/import/importBankAccountDetails',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.importHardwareTokens)
                        ? [
                              {
                                  key: 'import-otpDevices',
                                  label: language.imports.authenticationDevices,
                                  to: '/import/importOtpDevices',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                ],
                key: 'imports',
                label: language.imports.import,
                variant: 'button',
            });
        }

        if (
            hasOneAuthorization([
                ManagerAuthorization.exportDesktopTest,
                ManagerAuthorization.exportFundusFiles,
                ManagerAuthorization.exportPatientQuestionnaires,
            ])
        ) {
            items.push({
                icon: 'import',
                items: [
                    ...(hasOneAuthorization(ManagerAuthorization.exportPatientQuestionnaires)
                        ? [
                              {
                                  key: 'exports-patientSatisfaction',
                                  label: language.common.questionnaire,
                                  to: '/exports/patientSatisfaction',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.exportFundusFiles)
                        ? [
                              {
                                  key: 'exports-fundus',
                                  label: language.export.fundusBestanden,
                                  to: '/exports/fundus',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                ],
                key: 'exports',
                label: language.export.export,
                variant: 'button',
            });
        }

        if (
            hasOneAuthorization([
                ManagerAuthorization.deviceAndAccessoryTypes,
                ManagerAuthorization.embloomQuestionnaires,
                ManagerAuthorization.groupRead,
                ManagerAuthorization.insurersRead,
                ManagerAuthorization.managerAuthorizations,
                ManagerAuthorization.servicesRead,
                ManagerAuthorization.uzovisRead,
            ])
        ) {
            items.push({
                icon: 'settings',
                items: [
                    ...(hasOneAuthorization(ManagerAuthorization.servicesRead)
                        ? [
                              {
                                  key: 'services',
                                  label: language.service.services,
                                  to: '/services',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.groupRead)
                        ? [
                              {
                                  key: 'groups',
                                  label: language.groups.groups,
                                  to: '/groups',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.insurersRead)
                        ? [
                              {
                                  key: 'insurers',
                                  label: language.insurers.insurers,
                                  to: '/insurers',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.deviceAndAccessoryTypes)
                        ? [
                              {
                                  key: 'deviceTypes',
                                  label: language.deviceTypes.deviceTypes,
                                  to: '/deviceTypes',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.deviceAndAccessoryTypes)
                        ? [
                              {
                                  key: 'deviceAccessoryTypes',
                                  label: language.deviceAccessoryTypes.deviceAccessoryTypes,
                                  to: '/deviceAccessoryTypes',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.uzovisRead)
                        ? [
                              {
                                  key: 'uzovis',
                                  label: language.uzovi.uzovis,
                                  to: '/uzovis',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.embloomQuestionnaires)
                        ? [
                              {
                                  key: 'embloomQuestionnaires',
                                  label: language.embloomQuestionnaires.name,
                                  to: '/embloomQuestionnaires',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                    ...(hasOneAuthorization(ManagerAuthorization.managerAuthorizations)
                        ? [
                              {
                                  key: 'managerAuthorizations',
                                  label: language.common.managerAuthorizations,
                                  to: '/managerAuthorizations',
                                  variant: 'link' as const,
                              },
                          ]
                        : []),
                ],
                key: 'settings',
                label: language.setting.settings,
                variant: 'button',
            });
        }

        if (
            hasOneAuthorization(ManagerAuthorization.adminProcessRead) &&
            ['development', 'staging'].includes(frontendSettings.environment)
        ) {
            items.push({
                icon: 'information',
                key: 'fillDummyEctosenseReports',
                label: 'Fill Dummy Ectosense Reports',
                to: '/fillDummyEctosenseReports',
                variant: 'link',
            });
        }

        if (
            !isOnlyOrganizationPayoutManager &&
            hasBrightPlanAccess(currentProfession, frontendSettings.brightPlanIntegrationEnabled)
        ) {
            items.push({
                icon: 'external',
                key: 'brightPlan',
                label: 'BrightPlan',
                onClick: async () => await handleBrightPlanGetSsoUrl(),
                variant: 'button',
            });
        }

        if (frontendSettings.isAiChatbotSsoEnabled && hasOneAuthorization(ManagerAuthorization.chatbotSso)) {
            items.push({
                icon: 'external',
                key: 'chatbot',
                label: 'Chatbot',
                onClick: async () => await getChatbotSsoUrl(),
                variant: 'button',
            });
        }

        if (frontendSettings.environment !== 'production') {
            items.push({
                icon: 'delete',
                key: 'removePractitionerFirstUseTourSteps',
                label: language.common.removePractitionerFirstUseTourSteps,
                onClick: async () => await deletePractitionerFirstUseTourSteps(),
                variant: 'button',
            });
        }

        if (
            hasOneAuthorization([
                ManagerAuthorization.organizationAddEdit,
                ManagerAuthorization.practitionerAddEdit,
                ManagerAuthorization.jobs,
            ])
        ) {
            items.push({
                icon: 'warning',
                key: 'dataChecks',
                label: language.dataChecks.checks,
                to: '/dataChecks',
                variant: 'link',
            });
        }

        return items;
    }, [
        hasAccessToHomepage,
        bariPreSettings,
        bariPostSettings,
        osasSettings,
        isOnlyOrganizationPayoutManager,
        hasAccessToHolterDashboard,
        hasAccessToFundusNoShows,
        currentOrganization.id,
        currentProfession,
        frontendSettings,
        isManager,
        isKsyos,
        serviceConsumers,
    ]);
}
